import React from 'react';
import {
  Box,
  Typography,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  alpha,
  Grid,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import {
  HighQuality as HighQualityIcon,
  Speed as SpeedIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useDesigner } from '../../contexts/DesignerContext';

interface QualitySelectorProps {
  onQualitySelect?: (quality: 'low' | 'high') => void;
  readyToGenerate?: boolean;
}

const QualitySelector: React.FC<QualitySelectorProps> = ({
  onQualitySelect,
  readyToGenerate = false
}) => {
  const theme = useTheme();
  const { selections, setQuality } = useDesigner();
  
  const handleQualityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const quality = event.target.value as 'low' | 'high';
    setQuality(quality);
    
    if (onQualitySelect) {
      onQualitySelect(quality);
    }
  };

  // Icon components for quality options
  const IconLowQuality = () => <SpeedIcon sx={{ fontSize: 28 }} />;
  const IconHighQuality = () => <HighQualityIcon sx={{ fontSize: 28 }} />;

  // Quality options with details
  const qualityOptions = [
    {
      value: 'low',
      label: 'Niska',
      description: 'Szybsze generowanie, niższa jakość obrazu',
      IconComponent: IconLowQuality,
      benefits: ['Szybsze generowanie (15-30s)', 'Mniejszy rozmiar pliku', 'Dobra dla testów'],
      limitations: ['Może zawierać artefakty', 'Mniej detali', 'Niższa rozdzielczość']
    },
    {
      value: 'high',
      label: 'Wysoka',
      description: 'Lepszy obraz, dłuższy czas generowania',
      IconComponent: IconHighQuality,
      benefits: ['Lepsza jakość obrazu', 'Więcej detali', 'Wyższa rozdzielczość'],
      limitations: ['Dłuższy czas generowania (30-60s)', 'Większy rozmiar pliku']
    }
  ];

  return (
    <Box>
        {!readyToGenerate ? (
          <Box sx={{ 
            py: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            bgcolor: alpha(theme.palette.background.default, 0.5), 
            borderRadius: 2,
            border: `1px dashed ${alpha(theme.palette.text.secondary, 0.3)}`
          }}>
            <SettingsIcon sx={{ fontSize: 48, color: alpha(theme.palette.text.secondary, 0.5), mb: 2 }} />
            <Typography variant="body1" color="text.secondary" align="center">
              Najpierw wybierz zdjęcie źródłowe, rodzaj przeniesienia, model docelowy i studio.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Wybierz jakość generowanego obrazu:
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <RadioGroup
                value={selections.quality}
                onChange={handleQualityChange}
                name="quality-options"
              >
                <Grid container spacing={3}>
                  {qualityOptions.map((option) => (
                    <Grid item xs={12} sm={6} key={option.value}>
                      <Card
                        sx={{
                          border: selections.quality === option.value 
                            ? `2px solid ${theme.palette.primary.main}` 
                            : `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          boxShadow: selections.quality === option.value 
                            ? `0 4px 20px ${alpha(theme.palette.primary.main, 0.2)}` 
                            : 'none',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`
                          },
                          position: 'relative'
                        }}
                        onClick={() => setQuality(option.value as 'low' | 'high')}
                      >
                        <FormControlLabel
                          value={option.value}
                          control={<Radio sx={{ position: 'absolute', top: 16, right: 16 }} />}
                          label=""
                          sx={{ 
                            width: '100%', 
                            m: 0,
                            '& .MuiRadio-root': { opacity: 0 }
                          }}
                        />
                        
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Box 
                              sx={{ 
                                width: 48, 
                                height: 48, 
                                borderRadius: '50%', 
                                backgroundColor: selections.quality === option.value 
                                  ? theme.palette.primary.main
                                  : alpha(theme.palette.primary.main, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 2
                              }}
                            >
                              {option.IconComponent && <option.IconComponent />}
                            </Box>
                            <Box>
                              <Typography 
                                variant="h6" 
                                fontWeight={selections.quality === option.value ? 600 : 500}
                                sx={{ color: selections.quality === option.value ? 'primary.main' : 'text.primary' }}
                              >
                                {option.label}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {option.description}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                Zalety:
                              </Typography>
                              <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                                {option.benefits.map((benefit, idx) => (
                                  <Typography component="li" variant="body2" key={idx} sx={{ mb: 0.5 }}>
                                    {benefit}
                                  </Typography>
                                ))}
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                Ograniczenia:
                              </Typography>
                              <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                                {option.limitations.map((limitation, idx) => (
                                  <Typography component="li" variant="body2" key={idx} sx={{ mb: 0.5 }}>
                                    {limitation}
                                  </Typography>
                                ))}
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </RadioGroup>
            </Box>
            
            <Box sx={{ mt: 3, pt: 2, borderTop: `1px dashed ${alpha(theme.palette.divider, 0.5)}` }}>
              <Typography variant="body2" color="text.secondary">
                Wybrana jakość: <Typography component="span" fontWeight={600} color="primary.main">
                  {selections.quality === 'low' ? 'Niska' : 'Wysoka'}
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Po wybraniu jakości możesz przejść do generowania obrazu.
              </Typography>
            </Box>
          </>
        )}
    </Box>
  );
};

export default QualitySelector;